'use client'

import Link from 'next/link'
import React from 'react'
import { ArrowRight, ArrowUpRight } from 'lucide-react'
import { clinicIdentity } from '@/lib/clinic-identity'

// One navigation link per card, pinned to the card's foot (flex column +
// mt-auto) so the three links align across the row however long each
// description runs. P2.05: the Maps link is intentionally untracked.
const CARD =
  'flex flex-col bg-white rounded-2xl p-8 shadow-lg transition-transform duration-300 hover:-translate-y-2 group'
const CARD_LINK_ROW = 'mt-auto pt-6 text-center'
// Inline (not flex) so a label that wraps in a narrow card flows like text;
// each label's last word and arrow share a nowrap span so the arrow never
// wraps onto a line of its own.
const CARD_LINK =
  'rounded-sm box-decoration-clone font-medium text-emerald-700 underline underline-offset-4 hover:text-emerald-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2'
const CARD_LINK_ICON = 'ml-1.5 inline-block h-4 w-4 align-[-0.125em]'

const ReachDifferenceCards: React.FC = () => (
  <section className="py-20 px-4 bg-gray-50 rounded-2xl">
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-16">
        <h2 className="text-4xl lg:text-5xl font-serif text-gray-900 mb-4">
          The ReACH Difference: Care That Truly Connects
        </h2>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {/* Card 1 */}
        <div className={CARD}>
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-emerald-200">
            <svg
              className="w-8 h-8 text-emerald-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">
            Personalized, Empathetic Care
          </h3>
          <p className="text-gray-600 text-center leading-relaxed">
            We believe in treating the whole person, not just symptoms. Your
            journey is unique, and we take the time to build a genuine
            therapeutic relationship based on trust, respect, and understanding.
          </p>
          <p className={CARD_LINK_ROW}>
            <Link href="/team" className={CARD_LINK}>
              Meet Our{' '}
              <span className="whitespace-nowrap">
                Team
                <ArrowRight aria-hidden="true" className={CARD_LINK_ICON} />
              </span>
            </Link>
          </p>
        </div>

        {/* Card 2 */}
        <div className={CARD}>
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-emerald-200">
            <svg
              className="w-8 h-8 text-emerald-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">
            Evidence-Based Expertise
          </h3>
          <p className="text-gray-600 text-center leading-relaxed">
            Our psychiatrists are highly trained in the latest diagnostic tools
            and treatment modalities. We integrate proven, research-backed
            strategies to ensure you receive the most effective care, including
            structured programs.
          </p>
          <p className={CARD_LINK_ROW}>
            <Link
              href="/attention-deficit-hyperactivity-disorder-therapy"
              className={CARD_LINK}
            >
              Adult ADHD Therapy{' '}
              <span className="whitespace-nowrap">
                Program
                <ArrowRight aria-hidden="true" className={CARD_LINK_ICON} />
              </span>
            </Link>
          </p>
        </div>

        {/* Card 3 */}
        <div className={CARD}>
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-emerald-200">
            <svg
              className="w-8 h-8 text-emerald-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">
            Care That Fits Your Life
          </h3>
          <p className="text-gray-600 text-center leading-relaxed">
            Access expert psychiatric support seamlessly, whether you prefer
            confidential online sessions from the comfort of your home or
            in-person appointments at our welcoming clinic.
          </p>
          <p className={CARD_LINK_ROW}>
            <a
              href={clinicIdentity.maps.placeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={CARD_LINK}
            >
              Find Us on Google{' '}
              <span className="whitespace-nowrap">
                Maps
                <span className="sr-only"> (opens in a new tab)</span>
                <ArrowUpRight aria-hidden="true" className={CARD_LINK_ICON} />
              </span>
            </a>
          </p>
        </div>
      </div>
    </div>
  </section>
)

export default ReachDifferenceCards
